/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    useTheme,
    AppBar,
    Toolbar,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    CircularProgress,
    Snackbar,
    Divider,
    FormControlLabel,
    Switch,
    Stack,
    RadioGroup,
    Radio,
    FormLabel,
    Collapse,
    useMediaQuery,
} from '@mui/material';
import {
    ArrowBack,
    FlightTakeoff as FlightTakeoffOutlinedIcon,
    Person as PersonIcon,
    Group as GroupIcon,
    Info as InfoOutlinedIcon,
    AttachMoney as MoneyIcon,
    EventNote,
    Settings as SettingsIcon,
    ExpandMore,
    ExpandLess,
    Add,
    Remove,
    CloudUpload,
} from '@mui/icons-material';
import { api, isAuthenticated } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import LoginPromptModal from '../components/LoginPromptModal';
import TripImportCard from '../components/trips/TripImportCard';
import { todayLocalString, daysBetween } from '../utils/dateHelpers';

// ─── Types ───────────────────────────────────────────────

type CostSharingModel = 'split_equally' | 'custom_amounts' | 'organizer_pays' | 'individual_booking';

const COST_SHARING_OPTIONS: { value: CostSharingModel; label: string; description: string }[] = [
    { value: 'split_equally', label: 'Split Equally', description: 'Total cost divided by number of members' },
    { value: 'custom_amounts', label: 'Custom Amounts', description: 'Organizer assigns specific amounts' },
    { value: 'organizer_pays', label: 'Organizer Pays All', description: 'Single payer covers all expenses' },
    { value: 'individual_booking', label: 'Individual Booking', description: 'Each member books their own' },
];

// ─── Stepper Input Component ─────────────────────────────

interface StepperInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    label: string;
}

const StepperInput: React.FC<StepperInputProps> = ({ value, onChange, min = 1, max = 99, label }) => {
    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            bgcolor: 'white',
        }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton
                    size="small"
                    onClick={() => value > min && onChange(value - 1)}
                    disabled={value <= min}
                    sx={{ bgcolor: '#f5f5f5', width: 32, height: 32 }}
                >
                    <Remove fontSize="small" />
                </IconButton>
                <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
                    {value}
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => value < max && onChange(value + 1)}
                    disabled={value >= max}
                    sx={{ bgcolor: '#f5f5f5', width: 32, height: 32 }}
                >
                    <Add fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

// ─── Main Component ──────────────────────────────────────

const CreateTripPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isEnabled } = useFeatureFlags();
    const groupTripsEnabled = isEnabled('group_trips');
    const masterPublicEnabled = isEnabled('master_public');
    const masterFriendsEnabled = isEnabled('master_friends');
    const publicTripsEnabled = masterPublicEnabled && isEnabled('public_trips');
    const friendsTripsEnabled = masterFriendsEnabled && isEnabled('friends_trips');
    const today = todayLocalString();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dateError, setDateError] = useState('');
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [showImportCard, setShowImportCard] = useState(false);

    // ─── Trip Type ───────────────────────────────────────
    const [tripType, setTripType] = useState<'individual' | 'group'>('individual');

    // ─── Basic Fields (Both trip types) ──────────────────
    const [title, setTitle] = useState('');
    const [destination, setDestination] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState<'private' | 'friends_family' | 'public'>('private');
    const [expiresAt, setExpiresAt] = useState('');

    // ─── Group Trip Fields ───────────────────────────────
    const [groupName, setGroupName] = useState('');
    const [groupTravelers, setGroupTravelers] = useState(4); // Changed to number for stepper
    const [costSharingModel, setCostSharingModel] = useState<CostSharingModel>('split_equally');
    const [rsvpDeadline, setRsvpDeadline] = useState('');
    const [groupNotes, setGroupNotes] = useState('');
    const [showAdvancedPermissions, setShowAdvancedPermissions] = useState(false);
    const [showCostSharing, setShowCostSharing] = useState(false);
    const [allowMemberEdit, setAllowMemberEdit] = useState(false);
    const [allowMemberPhotos, setAllowMemberPhotos] = useState(true);
    const [allowMemberInvite, setAllowMemberInvite] = useState(false);
    const [allowMemberBook, setAllowMemberBook] = useState(false);
    const [allowMemberOptout, setAllowMemberOptout] = useState(true);

    // ─── Effects ─────────────────────────────────────────
    useEffect(() => {
        const authenticated = isAuthenticated();
        setUserIsAuthenticated(authenticated);

        const pendingTrip = localStorage.getItem('pendingTrip');
        if (pendingTrip) {
            try {
                const tripData = JSON.parse(pendingTrip);
                if (tripData.title) setTitle(tripData.title);
                if (tripData.destination) setDestination(tripData.destination);
                if (tripData.startDate) setStartDate(tripData.startDate);
                if (tripData.endDate) setEndDate(tripData.endDate);
                if (tripData.description) setDescription(tripData.description);
                if (tripData.visibility) setVisibility(tripData.visibility);
                if (tripData.tripType) setTripType(tripData.tripType);
                if (authenticated) localStorage.removeItem('pendingTrip');
            } catch (e) {
                localStorage.removeItem('pendingTrip');
            }
        }
    }, []);

    // Auto-fix end date if it's before start date
    useEffect(() => {
        if (startDate && endDate && endDate < startDate) {
            setEndDate(startDate);
            setDateError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate]);

    // ─── Computed ────────────────────────────────────────
    const tripDays = daysBetween(startDate, endDate);

    // ─── Handlers ────────────────────────────────────────
    const handleTripTypeChange = (newType: 'individual' | 'group') => {
        if (newType === 'group' && !userIsAuthenticated) {
            setShowLoginPrompt(true);
            return;
        }
        setTripType(newType);
    };

    const validateDates = (): boolean => {
        if (startDate && startDate < today) {
            setDateError('Start date cannot be in the past');
            return false;
        }
        if (endDate && endDate < today) {
            setDateError('End date cannot be in the past');
            return false;
        }
        if (startDate && endDate && endDate < startDate) {
            setDateError('End date must be after start date');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setDateError('');

        if (!title.trim()) {
            setError('Trip name is required');
            return;
        }
        if (!destination.trim()) {
            setError('Destination is required');
            return;
        }
        if (!validateDates()) {
            return;
        }

        if (!userIsAuthenticated) {
            localStorage.setItem('pendingTrip', JSON.stringify({
                title, destination, startDate, endDate, description, visibility, tripType,
            }));
            setShowLoginPrompt(true);
            return;
        }

        setLoading(true);

        try {
            const payload: any = {
                title,
                destination,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                description: description || undefined,
                visibility: tripType === 'group' ? 'friends_family' : visibility,
                is_group: tripType === 'group',
                expires_at: (tripType !== 'group' && visibility !== 'private' && expiresAt) ? expiresAt : undefined,
            };

            if (tripType === 'group') {
                payload.group_name = groupName || title;
                payload.cost_sharing_model = costSharingModel;
                payload.rsvp_deadline = rsvpDeadline || undefined;
                payload.allow_member_edit = allowMemberEdit;
                payload.allow_member_photos = allowMemberPhotos;
                payload.allow_member_invite = allowMemberInvite;
                payload.allow_member_book = allowMemberBook;
                payload.allow_member_optout = allowMemberOptout;
                payload.group_notes = groupNotes || undefined;
                payload.estimated_travelers = groupTravelers.toString();
            }

            const response = await api.post('/trips', payload);
            const data = await response.json();

            if (response.ok && data.success) {
                const tripId = data.trip.tripId;
                trackEvent('trip_created', { tripId });
                localStorage.removeItem('pendingTrip');
                
                setSnackbar({ 
                    open: true, 
                    message: tripType === 'group' 
                        ? '✅ Group trip created!' 
                        : '✅ Trip created!'
                });
                
                setTimeout(() => {
                    navigate(`/trips/${tripId}`);
                }, 500);
            } else {
                setError(data.error || 'Failed to create trip');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════

    return (
        <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
            {/* Header - Compact on mobile */}
            <AppBar 
                position="sticky" 
                color="transparent" 
                elevation={0} 
                sx={{ 
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                    <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Create Trip
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 3 }, pb: 10 }}>
                {/* Guest Notice - More compact */}
                {!userIsAuthenticated && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2, py: 0.5 }} icon={<InfoOutlinedIcon fontSize="small" />}>
                        <Typography variant="body2">Planning as guest. Log in to save.</Typography>
                    </Alert>
                )}

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                {dateError && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{dateError}</Alert>}

                {/* PDF Import Option (collapsed by default) */}
                <Card sx={{ mb: 2, borderRadius: 2, border: '1px dashed #2D6E6A', bgcolor: '#F0FAF8' }}>
                    {!showImportCard ? (
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <CloudUpload sx={{ color: '#2D6E6A', fontSize: 28 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Have a PDF itinerary?
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Import flights, hotels, and bookings automatically
                                </Typography>
                            </Box>
                            <Button
                                onClick={() => setShowImportCard(true)}
                                variant="text"
                                sx={{ color: '#2D6E6A', textTransform: 'none', fontWeight: 700, fontSize: '1.05rem', whiteSpace: 'nowrap', px: 2, py: 0.75 }}
                            >
                                Import
                            </Button>
                        </CardContent>
                    ) : (
                        <Box sx={{ p: 0 }}>
                            <TripImportCard
                                onImportComplete={(tripId: string) => navigate(`/trips/${tripId}`)}
                                onCancel={() => setShowImportCard(false)}
                            />
                        </Box>
                    )}
                </Card>

                {/* OR divider between Import and manual creation */}
                <Box sx={{ display: 'flex', alignItems: 'center', my: 3, gap: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" sx={{ color: '#999', fontWeight: 600, fontSize: '0.75rem', letterSpacing: 1 }}>
                        OR
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                </Box>

                {/* Form heading */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontFamily: 'Poppins, sans-serif', color: '#333' }}>
                    Build a new trip
                </Typography>

                <form onSubmit={handleSubmit}>
                    {/* ─── Trip Type Selector - Only show if group_trips enabled ─── */}
                    {groupTripsEnabled && (
                    <Card sx={{ mb: 2, borderRadius: 2 }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                                Trip Type
                            </Typography>
                            <ToggleButtonGroup
                                value={tripType}
                                exclusive
                                onChange={(_, v) => { if (v) handleTripTypeChange(v); }}
                                fullWidth
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        py: { xs: 1, sm: 1.5 },
                                        borderRadius: '8px !important',
                                        mx: 0.5,
                                        border: '2px solid transparent !important',
                                        '&.Mui-selected': {
                                            bgcolor: tripType === 'group' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(0,180,160,0.1)',
                                            color: tripType === 'group' ? '#1976D2' : '#2D6E6A',
                                            borderColor: tripType === 'group' ? '#1976D2 !important' : '#2D6E6A !important',
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="individual">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                        <Box sx={{ textAlign: 'left' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                Individual
                                            </Typography>
                                            {!isMobile && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Up to 6 travelers
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </ToggleButton>
                                <ToggleButton value="group">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <GroupIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                        <Box sx={{ textAlign: 'left' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                Group
                                            </Typography>
                                            {!isMobile && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Coordinate with others
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </CardContent>
                    </Card>
                    )}

                    {/* ─── Main Form Card ─────────────────────────── */}
                    <Card sx={{ 
                        borderRadius: 2, 
                        overflow: 'hidden',
                        border: tripType === 'group' ? '2px solid #1976D2' : '1px solid #e0e0e0',
                    }}>
                        {tripType === 'group' && <Box sx={{ height: 3, bgcolor: '#1976D2' }} />}
                        
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            {/* Required Fields */}
                            <TextField
                                fullWidth
                                size={isMobile ? 'small' : 'medium'}
                                label="Trip Name *"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g., Kenya Safari 2026"
                                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />

                            <TextField
                                fullWidth
                                size={isMobile ? 'small' : 'medium'}
                                label="Destination *"
                                value={destination}
                                onChange={(e) => setDestination(e.target.value)}
                                placeholder="e.g., Nairobi, Kenya"
                                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />

                            {/* Dates - Side by side */}
                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                <TextField
    fullWidth
    size={isMobile ? 'small' : 'medium'}
    label="Start Date"
    type="date"
    value={startDate}
    onChange={(e) => {
        const val = e.target.value;
        if (val < today) {
            setStartDate(today);
            setDateError('Start date cannot be in the past');
            setTimeout(() => setDateError(''), 3000);
            return;
        }
        setStartDate(val);
        if (endDate && endDate < val) setEndDate(val);
        setDateError('');
    }}
    InputLabelProps={{ shrink: true }}
    inputProps={{ min: today }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                <TextField
    fullWidth
    size={isMobile ? 'small' : 'medium'}
    label="End Date"
    type="date"
    value={endDate}
    onChange={(e) => {
        const val = e.target.value;
        const minDate = startDate || today;
        if (val < minDate) {
            setEndDate(minDate);
            setDateError('End date must be on or after start date');
            setTimeout(() => setDateError(''), 3000);
            return;
        }
        setEndDate(val);
        setDateError('');
    }}
    InputLabelProps={{ shrink: true }}
    inputProps={{ min: startDate || today }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Trip Duration - Compact chip */}
                            {tripDays > 0 && (
                                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EventNote sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {tripDays} {tripDays === 1 ? 'day' : 'days'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Description - Collapsible on mobile */}
                            <TextField
                                fullWidth
                                size={isMobile ? 'small' : 'medium'}
                                label={tripType === 'group' ? "Description *" : "Description (optional)"}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                multiline
                                rows={isMobile ? 2 : 3}
                                placeholder="What's this trip about?"
                                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />

                            {/* Visibility - Individual only */}
                            {tripType === 'individual' && userIsAuthenticated && (
                                <>
                                <FormControl fullWidth size={isMobile ? 'small' : 'medium'} sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                    <InputLabel>Visibility</InputLabel>
                                    <Select value={visibility} label="Visibility" onChange={(e) => setVisibility(e.target.value as any)}>
                                        <MenuItem value="private">🔒 Private</MenuItem>
                                        {friendsTripsEnabled && <MenuItem value="bffs_travel_partners">⭐ BFFs/Travel Partners</MenuItem>}
                                        {friendsTripsEnabled && <MenuItem value="friends_family">👥 Friends & Family</MenuItem>}
                                        {publicTripsEnabled && <MenuItem value="public">🌍 Public</MenuItem>}
                                    </Select>
                                </FormControl>
                                {visibility !== 'private' && (
                                    <TextField
                                        fullWidth
                                        label="Visibility expires (optional)"
                                        type="date"
                                        size="small"
                                        value={expiresAt}
                                        onChange={(e) => setExpiresAt(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                        helperText="Leave blank for no expiry"
                                        sx={{ mt: 1 }}
                                    />
                                )}
                                </>
                            )}

                            {/* ─── Group-Specific Fields ──────────────────── */}
                            {tripType === 'group' && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976D2' }}>
                                        Group Settings
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        size={isMobile ? 'small' : 'medium'}
                                        label="Group Name (optional)"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        placeholder="Defaults to trip name"
                                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />

                                    {/* Group Size - Stepper for mobile */}
                                    <Box sx={{ mb: 2 }}>
                                        <StepperInput
                                            value={groupTravelers}
                                            onChange={setGroupTravelers}
                                            min={2}
                                            max={50}
                                            label="Estimated Group Size"
                                        />
                                    </Box>

                                    {/* Cost Sharing - Collapsible */}
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => setShowCostSharing(!showCostSharing)}
                                            endIcon={showCostSharing ? <ExpandLess /> : <ExpandMore />}
                                            sx={{ 
                                                justifyContent: 'space-between', 
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                py: 1.5,
                                                borderColor: '#e0e0e0',
                                                color: 'text.primary',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <MoneyIcon fontSize="small" sx={{ color: '#1976D2' }} />
                                                <span>Cost Sharing: {COST_SHARING_OPTIONS.find(o => o.value === costSharingModel)?.label}</span>
                                            </Box>
                                        </Button>
                                        
                                        <Collapse in={showCostSharing}>
                                            <Box sx={{ pt: 2, pl: 1 }}>
                                                <RadioGroup
                                                    value={costSharingModel}
                                                    onChange={(e) => setCostSharingModel(e.target.value as CostSharingModel)}
                                                >
                                                    {COST_SHARING_OPTIONS.map((option) => (
                                                        <FormControlLabel
                                                            key={option.value}
                                                            value={option.value}
                                                            control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#1976D2' } }} />}
                                                            label={
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.label}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                                                                </Box>
                                                            }
                                                            sx={{ mb: 1, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { mt: 0.3 } }}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </Box>
                                        </Collapse>
                                    </Box>

                                    {/* RSVP Deadline */}
                                    <TextField
                                        fullWidth
                                        size={isMobile ? 'small' : 'medium'}
                                        label="RSVP Deadline (optional)"
                                        type="date"
                                        value={rsvpDeadline}
                                        onChange={(e) => setRsvpDeadline(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: today }}
                                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />

                                    {/* Group Notes */}
                                    <TextField
                                        fullWidth
                                        size={isMobile ? 'small' : 'medium'}
                                        label="Group Notes (optional)"
                                        value={groupNotes}
                                        onChange={(e) => setGroupNotes(e.target.value)}
                                        multiline
                                        rows={2}
                                        placeholder="Important info for members..."
                                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />

                                    {/* Advanced Permissions - Collapsible */}
                                    <Button
                                        fullWidth
                                        variant="text"
                                        startIcon={<SettingsIcon fontSize="small" />}
                                        endIcon={showAdvancedPermissions ? <ExpandLess /> : <ExpandMore />}
                                        onClick={() => setShowAdvancedPermissions(!showAdvancedPermissions)}
                                        sx={{ 
                                            textTransform: 'none', 
                                            color: 'text.secondary',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                        }}
                                    >
                                        Member Permissions
                                    </Button>
                                    
                                    <Collapse in={showAdvancedPermissions}>
                                        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                                            <Stack spacing={0}>
                                                <FormControlLabel
                                                    control={<Switch checked={allowMemberEdit} onChange={(e) => setAllowMemberEdit(e.target.checked)} size="small" />}
                                                    label={<Typography variant="body2">Edit itinerary</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={<Switch checked={allowMemberPhotos} onChange={(e) => setAllowMemberPhotos(e.target.checked)} size="small" />}
                                                    label={<Typography variant="body2">Add photos</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={<Switch checked={allowMemberInvite} onChange={(e) => setAllowMemberInvite(e.target.checked)} size="small" />}
                                                    label={<Typography variant="body2">Invite others</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={<Switch checked={allowMemberBook} onChange={(e) => setAllowMemberBook(e.target.checked)} size="small" />}
                                                    label={<Typography variant="body2">Book for themselves</Typography>}
                                                />
                                                <FormControlLabel
                                                    control={<Switch checked={allowMemberOptout} onChange={(e) => setAllowMemberOptout(e.target.checked)} size="small" />}
                                                    label={<Typography variant="body2">Opt out of items</Typography>}
                                                />
                                            </Stack>
                                        </Card>
                                    </Collapse>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* ─── Submit Button - Fixed on mobile ─────────── */}
                    <Box sx={{ 
                        mt: 2,
                        position: isMobile ? 'fixed' : 'relative',
                        bottom: isMobile ? 0 : 'auto',
                        left: isMobile ? 0 : 'auto',
                        right: isMobile ? 0 : 'auto',
                        p: isMobile ? 2 : 0,
                        bgcolor: isMobile ? 'background.paper' : 'transparent',
                        borderTop: isMobile ? '1px solid #e0e0e0' : 'none',
                        zIndex: 10,
                    }}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || !title.trim() || !destination.trim()}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (tripType === 'group' ? <GroupIcon /> : <FlightTakeoffOutlinedIcon />)}
                            sx={{
                                borderRadius: '24px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                py: 1.5,
                                fontFamily: 'Poppins, sans-serif',
                                bgcolor: tripType === 'group' ? '#1976D2' : '#2D6E6A',
                                '&:hover': { bgcolor: tripType === 'group' ? '#1565C0' : '#009688' },
                            }}
                        >
                            {loading ? 'Creating...' : 'Create Trip'}
                        </Button>
                    </Box>
                </form>
            </Container>

            {/* Login Prompt */}
            <LoginPromptModal
                open={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                title="Save Your Trip"
                message="Log in to save your trip. Your details have been saved."
                redirectPath="/trips/new"
                featureName="trip creation"
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
};

export default CreateTripPage;
